import { useState, useEffect, useRef, useCallback } from "react";
import { supabase } from "./supabase";

const PROCEDURES = [
  "Hair Transplant", "Hair Restoration", "Anti-Ageing Treatment",
  "Aesthetic Surgery", "Skin Rejuvenation", "Wellness Package",
  "Dental Treatment", "Weight Management", "Other"
];

const COUNTRIES = [
  "UAE","Saudi Arabia","Kuwait","Qatar","Bahrain","Oman",
  "Russia","Ukraine","Kazakhstan","Belarus","Georgia",
  "UK","Germany","France","Netherlands","Poland","Other"
];

export default function LandingPage() {
  // Read ref code from URL: ?ref=PRIYA30
  const refCode = new URLSearchParams(window.location.search).get("ref") || "";

  const [affiliate, setAffiliate] = useState(null);
  const [step, setStep] = useState("landing"); // landing | form | success
  const [partialId] = useState(() => crypto.randomUUID());

  const [form, setForm] = useState({
    name: "", email: "", phone: "", country: "",
    procedure: "", budget: "", consent: false, notes: ""
  });
  const [touched, setTouched] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const partialSaved = useRef({});

  // Load affiliate and record click
  useEffect(() => {
    if (!refCode) return;
    (async () => {
      const { data: aff } = await supabase
        .from("affiliates")
        .select("id, name, niche, country, code, commission_rate")
        .eq("code", refCode)
        .eq("status", "active")
        .single();

      if (aff) {
        setAffiliate(aff);
        // Record click
        const today = new Date().toISOString().slice(0, 10);
        const { data: existing } = await supabase
          .from("clicks")
          .select("id, count")
          .eq("affiliate_id", aff.id)
          .eq("date", today)
          .single();

        if (existing) {
          await supabase.from("clicks").update({ count: existing.count + 1 }).eq("id", existing.id);
        } else {
          await supabase.from("clicks").insert([{ affiliate_id: aff.id, date: today, count: 1 }]);
        }
      }
    })();
  }, [refCode]);

  // Partial capture — save field by field as patient types
  const capturePartial = useCallback(async (fieldName, value) => {
    const updated = { ...partialSaved.current, [fieldName]: value };
    partialSaved.current = updated;

    const record = {
      id: partialId,
      affiliate_id: affiliate?.id || null,
      affiliate_code: refCode,
      last_field: fieldName,
      status: "partial",
      ...updated,
    };

    await supabase.from("partial_leads").upsert([record], { onConflict: "id" });
  }, [affiliate, refCode, partialId]);

  const handleChange = (field, value) => {
    setForm(f => ({ ...f, [field]: value }));
    setTouched(t => ({ ...t, [field]: true }));
    if (errors[field]) setErrors(e => ({ ...e, [field]: null }));
    if (field !== "consent") capturePartial(field, value);
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Your name is required";
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) e.email = "Valid email required";
    if (!form.phone.trim()) e.phone = "Phone number required";
    if (!form.country) e.country = "Please select your country";
    if (!form.procedure) e.procedure = "Please select a procedure";
    if (!form.consent) e.consent = "Please provide consent to proceed";
    return e;
  };

  const handleSubmit = async () => {
    const e = validate();
    if (Object.keys(e).length > 0) { setErrors(e); return; }
    setSubmitting(true);

    const lead = {
      affiliate_id: affiliate?.id || null,
      name: form.name,
      email: form.email,
      phone: form.phone,
      country: form.country,
      procedure: form.procedure,
      budget: form.budget,
      notes: form.notes,
      status: "inquiry",
      billing_amount: 0,
      commission_amount: 0,
      source: "landing_page",
      ref_code: refCode,
    };

    const { error } = await supabase.from("leads").insert([lead]);
    if (error) { setSubmitting(false); setErrors({ name: "Submission failed, please try again." }); return; }

    // Remove partial record
    await supabase.from("partial_leads").delete().eq("id", partialId);

    setSubmitting(false);
    setStep("success");
  };

  const inputCls = (field) => ({
    width: "100%",
    background: errors[field] ? "rgba(239,68,68,0.05)" : "rgba(255,255,255,0.04)",
    border: `1px solid ${errors[field] ? "#ef4444" : touched[field] && form[field] ? "#c9a96e" : "rgba(255,255,255,0.1)"}`,
    borderRadius: 10,
    padding: "14px 16px",
    color: "#f5f0e8",
    fontSize: 14,
    fontFamily: "'Cormorant Garamond', Georgia, serif",
    letterSpacing: "0.02em",
    transition: "border-color 0.2s",
    outline: "none",
    marginBottom: 4,
  });

  return (
    <div style={{ background: "#0e0b08", minHeight: "100vh", color: "#f5f0e8", fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;1,300;1,400&family=Montserrat:wght@300;400;500;600&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        input::placeholder, textarea::placeholder { color: rgba(245,240,232,0.3); }
        select option { background: #1a1208; color: #f5f0e8; }
        input:focus, select:focus, textarea:focus { border-color: #c9a96e !important; background: rgba(201,169,110,0.06) !important; }
        @keyframes fadeUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes pulse { 0%,100% { transform: scale(1); } 50% { transform: scale(1.05); } }
        @keyframes spin { to { transform: rotate(360deg); } }
        .fade-up { animation: fadeUp 0.7s ease forwards; }
        .fade-up-2 { animation: fadeUp 0.7s 0.15s ease both; }
        .fade-up-3 { animation: fadeUp 0.7s 0.3s ease both; }
        .fade-up-4 { animation: fadeUp 0.7s 0.45s ease both; }
        .btn-gold:hover { transform: translateY(-2px); box-shadow: 0 12px 40px rgba(201,169,110,0.3); }
        .btn-gold { transition: all 0.2s; }
        .proc-opt:hover { border-color: #c9a96e !important; background: rgba(201,169,110,0.08) !important; }
        .trust-card:hover { border-color: rgba(201,169,110,0.3) !important; transform: translateY(-2px); }
        .trust-card { transition: all 0.2s; }
      `}</style>

      {/* Nav */}
      <nav style={{ padding: "20px 40px", display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
        <img src="/logo.png" alt="Incostra Medical Tourism" style={{ height: 52, objectFit: "contain", filter: "brightness(0) invert(1)" }} />
        {affiliate && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, background: "rgba(201,169,110,0.08)", border: "1px solid rgba(201,169,110,0.2)", borderRadius: 30, padding: "8px 16px" }}>
            <div style={{ width: 28, height: 28, borderRadius: 14, background: "linear-gradient(135deg, #c9a96e, #8b6914)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 600, color: "#0e0b08", fontFamily: "'Montserrat', sans-serif" }}>
              {affiliate.name.charAt(0)}
            </div>
            <div>
              <div style={{ fontSize: 10, color: "#c9a96e", fontFamily: "'Montserrat', sans-serif", letterSpacing: "0.1em" }}>REFERRED BY</div>
              <div style={{ fontSize: 13, color: "#f5f0e8" }}>{affiliate.name}</div>
            </div>
          </div>
        )}
      </nav>

      {step === "landing" && (
        <>
          {/* Hero */}
          <section style={{ textAlign: "center", padding: "80px 24px 60px" }}>
            {affiliate && (
              <div className="fade-up" style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(201,169,110,0.1)", border: "1px solid rgba(201,169,110,0.25)", borderRadius: 30, padding: "8px 20px", marginBottom: 32, fontSize: 13, color: "#c9a96e", fontFamily: "'Montserrat', sans-serif", letterSpacing: "0.05em" }}>
                <span style={{ width: 6, height: 6, borderRadius: 3, background: "#c9a96e", display: "inline-block" }} />
                {affiliate.name}, {affiliate.niche} · {affiliate.country} recommends Incostra
              </div>
            )}
            <div className="fade-up-2" style={{ fontSize: "clamp(36px, 6vw, 68px)", fontWeight: 300, lineHeight: 1.1, color: "#f5f0e8", marginBottom: 16 }}>
              World-Class Care.<br />
              <em style={{ fontStyle: "italic", color: "#c9a96e" }}>India's Best Clinics.</em>
            </div>
            <div className="fade-up-3" style={{ fontSize: 18, fontWeight: 300, color: "rgba(245,240,232,0.55)", maxWidth: 540, margin: "0 auto 40px", lineHeight: 1.7, fontFamily: "'Montserrat', sans-serif" }}>
              Anti-ageing, aesthetics, hair restoration and wellness — at a fraction of Western prices, with no compromise on quality.
            </div>
            <button className="btn-gold fade-up-4" onClick={() => setStep("form")}
              style={{ background: "#c9a96e", border: "none", borderRadius: 6, padding: "16px 40px", color: "#0e0b08", fontSize: 13, fontWeight: 600, letterSpacing: "0.12em", fontFamily: "'Montserrat', sans-serif", cursor: "pointer" }}>
              REQUEST FREE CONSULTATION
            </button>
            <div className="fade-up-4" style={{ marginTop: 16, fontSize: 12, color: "rgba(245,240,232,0.3)", fontFamily: "'Montserrat', sans-serif", letterSpacing: "0.05em" }}>
              No commitment · Confidential · Response within 24h
            </div>
          </section>

          {/* Affiliate trust block */}
          {affiliate && (
            <section style={{ maxWidth: 680, margin: "0 auto 60px", padding: "0 24px" }}>
              <div style={{ background: "linear-gradient(135deg, rgba(201,169,110,0.08), rgba(201,169,110,0.03))", border: "1px solid rgba(201,169,110,0.2)", borderRadius: 16, padding: "28px 32px", display: "flex", gap: 24, alignItems: "center" }}>
                <div style={{ width: 56, height: 56, borderRadius: 28, background: "linear-gradient(135deg, #c9a96e, #8b6914)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, fontWeight: 600, color: "#0e0b08", flexShrink: 0, fontFamily: "'Montserrat', sans-serif" }}>
                  {affiliate.name.charAt(0)}
                </div>
                <div>
                  <div style={{ fontSize: 11, letterSpacing: "0.15em", color: "#c9a96e", fontFamily: "'Montserrat', sans-serif", marginBottom: 6 }}>TRUSTED REFERRAL</div>
                  <div style={{ fontSize: 20, color: "#f5f0e8", marginBottom: 4 }}><strong style={{ fontWeight: 500 }}>{affiliate.name}</strong> — {affiliate.niche}</div>
                  <div style={{ fontSize: 14, color: "rgba(245,240,232,0.5)", lineHeight: 1.6 }}>
                    {affiliate.name.split(" ")[0]} personally vetted Incostra's clinic partners and trusts us with their clients.
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* Trust signals */}
          <section style={{ maxWidth: 860, margin: "0 auto 80px", padding: "0 24px" }}>
            <div style={{ textAlign: "center", fontSize: 11, letterSpacing: "0.2em", color: "rgba(245,240,232,0.3)", fontFamily: "'Montserrat', sans-serif", marginBottom: 32 }}>WHY INCOSTRA</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16 }}>
              {[
                { icon: "✦", title: "Curated Clinics", body: "Only JCI-accredited or equivalent partner clinics in India. Every facility personally evaluated." },
                { icon: "◈", title: "Save 60–80%", body: "The same procedure that costs £10,000 in London can be done for £2,000–£3,500 in India." },
                { icon: "⬡", title: "End-to-End Care", body: "Visa support, airport transfers, accommodation, clinic coordination — all handled for you." },
              ].map((c, i) => (
                <div key={i} className="trust-card" style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 12, padding: "24px 22px" }}>
                  <div style={{ fontSize: 20, color: "#c9a96e", marginBottom: 14 }}>{c.icon}</div>
                  <div style={{ fontSize: 16, fontWeight: 500, color: "#f5f0e8", marginBottom: 8 }}>{c.title}</div>
                  <div style={{ fontSize: 13, color: "rgba(245,240,232,0.45)", lineHeight: 1.7, fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}>{c.body}</div>
                </div>
              ))}
            </div>
          </section>

          {/* Procedures grid */}
          <section style={{ maxWidth: 860, margin: "0 auto 80px", padding: "0 24px" }}>
            <div style={{ textAlign: "center", fontSize: 11, letterSpacing: "0.2em", color: "rgba(245,240,232,0.3)", fontFamily: "'Montserrat', sans-serif", marginBottom: 12 }}>TREATMENTS WE FACILITATE</div>
            <div style={{ textAlign: "center", fontSize: 32, fontWeight: 300, marginBottom: 36 }}>What brings you to India?</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10 }}>
              {[
                { name: "Hair Transplant", icon: "◉", sub: "from $1,800" },
                { name: "Anti-Ageing", icon: "✧", sub: "from $900" },
                { name: "Aesthetic Surgery", icon: "◈", sub: "from $2,200" },
                { name: "Skin Rejuvenation", icon: "◇", sub: "from $600" },
                { name: "Wellness Package", icon: "⬡", sub: "from $800" },
                { name: "Hair Restoration", icon: "◉", sub: "from $1,200" },
                { name: "Dental Treatment", icon: "◎", sub: "from $400" },
                { name: "Weight Management", icon: "✦", sub: "from $1,100" },
              ].map((p, i) => (
                <div key={i} className="proc-opt"
                  onClick={() => { setStep("form"); setTimeout(() => handleChange("procedure", p.name), 100); }}
                  style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 10, padding: "18px 14px", textAlign: "center", cursor: "pointer", transition: "all 0.2s" }}>
                  <div style={{ fontSize: 18, color: "#c9a96e", marginBottom: 8 }}>{p.icon}</div>
                  <div style={{ fontSize: 13, color: "#f5f0e8", marginBottom: 4 }}>{p.name}</div>
                  <div style={{ fontSize: 11, color: "#c9a96e", fontFamily: "'Montserrat', sans-serif" }}>{p.sub}</div>
                </div>
              ))}
            </div>
          </section>

          {/* Bottom CTA */}
          <section style={{ textAlign: "center", padding: "60px 24px 80px", borderTop: "1px solid rgba(255,255,255,0.05)" }}>
            <div style={{ fontSize: 32, fontWeight: 300, marginBottom: 12 }}>Ready to take the first step?</div>
            <div style={{ fontSize: 15, color: "rgba(245,240,232,0.4)", marginBottom: 32, fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}>Free consultation · No obligation · Confidential</div>
            <button className="btn-gold" onClick={() => setStep("form")}
              style={{ background: "#c9a96e", border: "none", borderRadius: 6, padding: "16px 40px", color: "#0e0b08", fontSize: 13, fontWeight: 600, letterSpacing: "0.12em", fontFamily: "'Montserrat', sans-serif", cursor: "pointer" }}>
              GET MY FREE CONSULTATION
            </button>
          </section>
        </>
      )}

      {step === "form" && (
        <section style={{ maxWidth: 580, margin: "0 auto", padding: "60px 24px 80px" }}>
          <button onClick={() => setStep("landing")} style={{ background: "none", border: "none", color: "rgba(245,240,232,0.35)", fontSize: 13, fontFamily: "'Montserrat', sans-serif", cursor: "pointer", marginBottom: 32, letterSpacing: "0.05em" }}>
            ← Back
          </button>

          {affiliate && (
            <div className="fade-up" style={{ display: "flex", alignItems: "center", gap: 12, background: "rgba(201,169,110,0.07)", border: "1px solid rgba(201,169,110,0.2)", borderRadius: 10, padding: "14px 18px", marginBottom: 32 }}>
              <div style={{ width: 36, height: 36, borderRadius: 18, background: "linear-gradient(135deg, #c9a96e, #8b6914)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 600, color: "#0e0b08", flexShrink: 0, fontFamily: "'Montserrat', sans-serif" }}>
                {affiliate.name.charAt(0)}
              </div>
              <div>
                <div style={{ fontSize: 12, color: "#c9a96e", fontFamily: "'Montserrat', sans-serif", letterSpacing: "0.08em" }}>REFERRED BY {affiliate.name.toUpperCase()}</div>
                <div style={{ fontSize: 13, color: "rgba(245,240,232,0.6)", marginTop: 2 }}>Your inquiry will be handled with the care {affiliate.name.split(" ")[0]} expects for their clients.</div>
              </div>
            </div>
          )}

          <div className="fade-up-2" style={{ fontSize: 36, fontWeight: 300, lineHeight: 1.2, marginBottom: 8 }}>Tell us about<br /><em style={{ color: "#c9a96e" }}>your goals</em></div>
          <div className="fade-up-3" style={{ fontSize: 14, color: "rgba(245,240,232,0.4)", fontFamily: "'Montserrat', sans-serif", fontWeight: 300, marginBottom: 36, lineHeight: 1.6 }}>
            Completely free and confidential. Our team will reach out within 24 hours.
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {/* Name */}
            <div>
              <label style={{ fontSize: 11, letterSpacing: "0.12em", color: "rgba(245,240,232,0.4)", fontFamily: "'Montserrat', sans-serif", display: "block", marginBottom: 6 }}>FULL NAME *</label>
              <input style={inputCls("name")} placeholder="Your full name" value={form.name} onChange={e => handleChange("name", e.target.value)} />
              {errors.name && <div style={{ fontSize: 11, color: "#ef4444", fontFamily: "'Montserrat', sans-serif", marginTop: 4 }}>{errors.name}</div>}
            </div>

            {/* Email + Phone */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <label style={{ fontSize: 11, letterSpacing: "0.12em", color: "rgba(245,240,232,0.4)", fontFamily: "'Montserrat', sans-serif", display: "block", marginBottom: 6 }}>EMAIL *</label>
                <input style={inputCls("email")} placeholder="your@email.com" type="email" value={form.email} onChange={e => handleChange("email", e.target.value)} />
                {errors.email && <div style={{ fontSize: 11, color: "#ef4444", fontFamily: "'Montserrat', sans-serif", marginTop: 4 }}>{errors.email}</div>}
              </div>
              <div>
                <label style={{ fontSize: 11, letterSpacing: "0.12em", color: "rgba(245,240,232,0.4)", fontFamily: "'Montserrat', sans-serif", display: "block", marginBottom: 6 }}>WHATSAPP / PHONE *</label>
                <input style={inputCls("phone")} placeholder="+44 7911 123456" value={form.phone} onChange={e => handleChange("phone", e.target.value)} />
                {errors.phone && <div style={{ fontSize: 11, color: "#ef4444", fontFamily: "'Montserrat', sans-serif", marginTop: 4 }}>{errors.phone}</div>}
              </div>
            </div>

            {/* Country */}
            <div>
              <label style={{ fontSize: 11, letterSpacing: "0.12em", color: "rgba(245,240,232,0.4)", fontFamily: "'Montserrat', sans-serif", display: "block", marginBottom: 6 }}>YOUR COUNTRY *</label>
              <select style={{ ...inputCls("country"), cursor: "pointer" }} value={form.country} onChange={e => handleChange("country", e.target.value)}>
                <option value="">Select your country</option>
                {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              {errors.country && <div style={{ fontSize: 11, color: "#ef4444", fontFamily: "'Montserrat', sans-serif", marginTop: 4 }}>{errors.country}</div>}
            </div>

            {/* Procedure */}
            <div>
              <label style={{ fontSize: 11, letterSpacing: "0.12em", color: "rgba(245,240,232,0.4)", fontFamily: "'Montserrat', sans-serif", display: "block", marginBottom: 10 }}>I'M INTERESTED IN *</label>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                {PROCEDURES.map(p => (
                  <button key={p} onClick={() => handleChange("procedure", p)}
                    style={{ background: form.procedure === p ? "rgba(201,169,110,0.15)" : "rgba(255,255,255,0.03)", border: `1px solid ${form.procedure === p ? "#c9a96e" : "rgba(255,255,255,0.08)"}`, borderRadius: 8, padding: "10px 14px", color: form.procedure === p ? "#c9a96e" : "rgba(245,240,232,0.5)", fontSize: 12, fontFamily: "'Montserrat', sans-serif", textAlign: "left", cursor: "pointer", transition: "all 0.15s" }}>
                    {p}
                  </button>
                ))}
              </div>
              {errors.procedure && <div style={{ fontSize: 11, color: "#ef4444", fontFamily: "'Montserrat', sans-serif", marginTop: 6 }}>{errors.procedure}</div>}
            </div>

            {/* Budget */}
            <div>
              <label style={{ fontSize: 11, letterSpacing: "0.12em", color: "rgba(245,240,232,0.4)", fontFamily: "'Montserrat', sans-serif", display: "block", marginBottom: 10 }}>APPROXIMATE BUDGET (OPTIONAL)</label>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {["Under $2,000","$2,000–$5,000","$5,000–$10,000","$10,000+","Not sure yet"].map(b => (
                  <button key={b} onClick={() => handleChange("budget", b)}
                    style={{ background: form.budget === b ? "rgba(201,169,110,0.15)" : "rgba(255,255,255,0.03)", border: `1px solid ${form.budget === b ? "#c9a96e" : "rgba(255,255,255,0.08)"}`, borderRadius: 20, padding: "7px 14px", color: form.budget === b ? "#c9a96e" : "rgba(245,240,232,0.45)", fontSize: 12, fontFamily: "'Montserrat', sans-serif", cursor: "pointer", transition: "all 0.15s" }}>
                    {b}
                  </button>
                ))}
              </div>
            </div>

            {/* Notes */}
            <div>
              <label style={{ fontSize: 11, letterSpacing: "0.12em", color: "rgba(245,240,232,0.4)", fontFamily: "'Montserrat', sans-serif", display: "block", marginBottom: 6 }}>ANYTHING ELSE (OPTIONAL)</label>
              <textarea style={{ ...inputCls("notes"), resize: "none", height: 80 }}
                placeholder="Any existing conditions, timeline, specific questions..."
                value={form.notes} onChange={e => handleChange("notes", e.target.value)} />
            </div>

            {/* GDPR Consent — required */}
            <div style={{ padding: "14px 16px", background: "rgba(255,255,255,0.03)", border: `1px solid ${errors.consent ? "#ef4444" : "rgba(255,255,255,0.08)"}`, borderRadius: 10 }}>
              <label style={{ display: "flex", gap: 12, alignItems: "flex-start", cursor: "pointer" }}>
                <input type="checkbox" checked={form.consent} onChange={e => handleChange("consent", e.target.checked)}
                  style={{ marginTop: 3, accentColor: "#c9a96e", width: 16, height: 16, flexShrink: 0 }} />
                <span style={{ fontSize: 12, color: "rgba(245,240,232,0.5)", fontFamily: "'Montserrat', sans-serif", lineHeight: 1.6 }}>
                  I consent to Incostra Medical Tourism (Swayam Private Limited) storing and processing my personal data to respond to this enquiry, in accordance with their{" "}
                  <a href="/privacy" style={{ color: "#c9a96e", textDecoration: "underline" }}>Privacy Policy</a>. *
                </span>
              </label>
              {errors.consent && <div style={{ fontSize: 11, color: "#ef4444", fontFamily: "'Montserrat', sans-serif", marginTop: 8 }}>{errors.consent}</div>}
            </div>

            {/* Submit */}
            <button className="btn-gold" onClick={handleSubmit} disabled={submitting}
              style={{ background: submitting ? "#8b6914" : "#c9a96e", border: "none", borderRadius: 8, padding: "18px", color: "#0e0b08", fontSize: 13, fontWeight: 600, letterSpacing: "0.12em", fontFamily: "'Montserrat', sans-serif", cursor: submitting ? "not-allowed" : "pointer", marginTop: 8, display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}>
              {submitting ? (
                <><div style={{ width: 16, height: 16, border: "2px solid #0e0b08", borderTopColor: "transparent", borderRadius: 8, animation: "spin 0.8s linear infinite" }} /> Submitting...</>
              ) : "REQUEST FREE CONSULTATION →"}
            </button>
          </div>
        </section>
      )}

      {step === "success" && (
        <section style={{ maxWidth: 520, margin: "0 auto", padding: "100px 24px", textAlign: "center" }}>
          <img src="/logo.png" alt="Incostra" style={{ height: 56, objectFit: "contain", margin: "0 auto 28px", display: "block", filter: "brightness(0) invert(1)", opacity: 0.9 }} />
          <div className="fade-up" style={{ fontSize: 42, fontWeight: 300, lineHeight: 1.15, marginBottom: 16 }}>
            Thank you,<br /><em style={{ color: "#c9a96e" }}>{form.name.split(" ")[0]}</em>
          </div>
          <div className="fade-up-2" style={{ fontSize: 16, color: "rgba(245,240,232,0.5)", lineHeight: 1.8, fontFamily: "'Montserrat', sans-serif", fontWeight: 300, marginBottom: 36 }}>
            Your consultation request has been received. Our team will reach out to <strong style={{ color: "#f5f0e8" }}>{form.email}</strong> within 24 hours.
          </div>
          {affiliate && (
            <div className="fade-up-3" style={{ background: "rgba(201,169,110,0.07)", border: "1px solid rgba(201,169,110,0.2)", borderRadius: 12, padding: "20px 24px" }}>
              <div style={{ fontSize: 13, color: "rgba(245,240,232,0.45)", fontFamily: "'Montserrat', sans-serif", marginBottom: 4 }}>Referred by</div>
              <div style={{ fontSize: 18, color: "#c9a96e" }}>{affiliate.name} · {affiliate.niche}</div>
            </div>
          )}
        </section>
      )}

      <footer style={{ borderTop: "1px solid rgba(255,255,255,0.05)", padding: "24px 40px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ fontSize: 12, color: "rgba(245,240,232,0.2)", fontFamily: "'Montserrat', sans-serif" }}>© 2026 Incostra · Swayam Private Limited · Gurugram, India</div>
        <div style={{ fontSize: 12, color: "rgba(245,240,232,0.2)", fontFamily: "'Montserrat', sans-serif" }}>GDPR Compliant · Data stored in EU</div>
      </footer>
    </div>
  );
}
